import { createSlice, type PayloadAction } from '@reduxjs/toolkit';
import type { RootState } from '@/store/rootState';

export type TriggerAuthoringPhase = 'browse' | 'setup' | 'record' | 'editWait';
export type TriggerAuthoringStep = 1 | 2 | 3 | 3.5;
export type TriggerWaitMode = 'hold' | 'animation' | 'hidden';

export interface TriggerAuthoringState {
  phase: TriggerAuthoringPhase;
  step: TriggerAuthoringStep;
  draftGateId: string | null;
  type: string | null;
  originAssetId: string | null;
  time: number;
  waitMode: TriggerWaitMode | null;
  startedAt: number;
  // The draft gate lives in one scene only, so opening another has to end the sequence
  // rather than carry a dead instance id into it.
  sceneInstanceId: string | null;
  // Seconds of clear space to leave after the gate when pushing the origin asset's crossing
  // clips. Captured at the timeline, whose zoom sets it; the setup panel cannot reach it.
  gapSeconds: number;
  // Setup picked up again on an unfinished trigger rather than started on a new one
  resumed: boolean;
}

const initialState: TriggerAuthoringState = {
  phase: 'browse',
  step: 1,
  draftGateId: null,
  type: null,
  originAssetId: null,
  time: 0,
  waitMode: null,
  startedAt: 0,
  sceneInstanceId: null,
  gapSeconds: 0,
  resumed: false,
};

const slice = createSlice({
  name: 'triggerAuthoring',
  initialState,
  reducers: {
    setupStarted: (
      state,
      action: PayloadAction<{
        originAssetId: string;
        time: number;
        gapSeconds: number;
        startedAt: number;
        sceneInstanceId: string;
        resumed?: boolean;
      }>
    ) => {
      state.phase = 'setup';
      state.step = 1;
      state.draftGateId = null;
      state.type = null;
      state.originAssetId = action.payload.originAssetId;
      state.time = action.payload.time;
      state.waitMode = null;
      state.startedAt = action.payload.startedAt;
      state.sceneInstanceId = action.payload.sceneInstanceId;
      state.gapSeconds = action.payload.gapSeconds;
      state.resumed = !!action.payload.resumed;
    },
    draftGateSet: (state, action: PayloadAction<{ draftGateId: string; type: string }>) => {
      state.draftGateId = action.payload.draftGateId;
      state.type = action.payload.type;
    },
    stepSet: (state, action: PayloadAction<TriggerAuthoringStep>) => {
      state.step = action.payload;
    },
    waitModeSet: (state, action: PayloadAction<TriggerWaitMode>) => {
      state.waitMode = action.payload;
    },
    setupEnded: () => initialState,
  },
});

export const { setupStarted, draftGateSet, stepSet, waitModeSet, setupEnded } = slice.actions;

export default slice.reducer;

export const selectTriggerAuthoring = (state: RootState): TriggerAuthoringState => state.triggerAuthoring;

export const selectIsTriggerSetup = (state: RootState): boolean => state.triggerAuthoring.phase === 'setup';

export const selectIsTriggerPlacing = (state: RootState): boolean =>
  state.triggerAuthoring.phase === 'setup' && state.triggerAuthoring.step === 2;
